import { FC, useEffect, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CapIQSuggestion } from 'api/models/CapIQSuggestion';
import { CommonCompanyProfileSuggestResponse } from 'api/models/CommonCompanyProfileSuggestResponse';
import { useSuggestCommonProfile } from 'services/hooks/companies';
import { CommonCompanyProfileFormProps, ProfileFormState, SaveConflict } from './CommonCompanyProfileForm.props';
import EdgarIssuerNamesSection from '../EdgarIssuerNamesSection';

interface SuggestionRowProps {
  label: string;
  suggestion: CapIQSuggestion;
  onAccept: (value: string) => void;
}

const SuggestionRow: FC<SuggestionRowProps> = ({ label, suggestion, onAccept }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    <Tooltip
      title={suggestion.already_in_use ? `Already used by "${suggestion.in_use_by_profile_name}" — cannot be accepted` : 'Click to apply'}
      placement="right"
    >
      <span>
        <Paper
          variant="outlined"
          onClick={suggestion.already_in_use ? undefined : () => onAccept(suggestion.value)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            cursor: suggestion.already_in_use ? 'default' : 'pointer',
            opacity: suggestion.already_in_use ? 0.5 : 1,
            '&:hover': suggestion.already_in_use ? {} : { bgcolor: 'action.hover' },
          }}
        >
          <Typography variant="body2">{suggestion.matched_name}</Typography>
          <Chip
            label={`${Math.round(suggestion.confidence * 100)}%`}
            size="small"
            color={suggestion.already_in_use ? 'warning' : 'info'}
            variant="outlined"
          />
          <Typography variant="body2" color="text.secondary">
            {suggestion.value}
          </Typography>
        </Paper>
      </span>
    </Tooltip>
  </Box>
);

const CommonCompanyProfileForm: FC<CommonCompanyProfileFormProps> = ({
  values,
  onChange,
  isLoading,
  profileId,
  onPendingEdgarAddsChange,
  onPendingEdgarRemovesChange,
  onHasConflicts,
  saveConflicts,
}) => {
  const [suggestions, setSuggestions] = useState<CommonCompanyProfileSuggestResponse | null>(null);

  const suggestMutation = useSuggestCommonProfile();

  const handleChange = (field: keyof ProfileFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...values, [field]: e.target.value });
  };

  const handleSuggest = async () => {
    const result = await suggestMutation.mutateAsync({
      name: values.name,
      cap_iq_id: values.cap_iq_id || null,
      sacra_domain: values.sacra_domain || null,
      zanbato_id: values.zanbato_id || null,
      exclude_profile_id: profileId ?? null,
    });
    setSuggestions(result);
  };

  const hasConflicts = (suggestions?.conflicts ?? []).length > 0;

  useEffect(() => {
    onHasConflicts?.(hasConflicts);
  }, [hasConflicts, onHasConflicts]);

  const handleAccept = (field: keyof ProfileFormState) => (value: string) => {
    onChange({ ...values, [field]: value });
    setSuggestions(prev => prev ? { ...prev, [`${field}_suggestion`]: null as any } : null);
  };

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <TextField
        label="Name"
        required
        fullWidth
        value={values.name}
        onChange={handleChange('name')}
        disabled={isLoading}
        variant="standard"
      />
      <TextField
        label="Cap IQ ID"
        fullWidth
        value={values.cap_iq_id}
        onChange={handleChange('cap_iq_id')}
        disabled={isLoading}
        variant="standard"
      />
      <TextField
        label="Sacra Domain"
        fullWidth
        value={values.sacra_domain}
        onChange={handleChange('sacra_domain')}
        helperText='Bare domain e.g. "stripe.com"'
        disabled={isLoading}
        variant="standard"
      />
      <TextField
        label="Zanbato ID"
        fullWidth
        value={values.zanbato_id}
        onChange={handleChange('zanbato_id')}
        disabled={isLoading}
        variant="standard"
      />

      {saveConflicts && saveConflicts.length > 0 && (
        <Alert severity="error">
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Could not save — the following fields are already in use:
          </Typography>
          {saveConflicts.map((c: SaveConflict) => (
            <Typography key={c.conflict_field} variant="body2">
              <strong>{c.conflict_field}</strong> is already used by &ldquo;{c.conflict_profile.name}&rdquo;
            </Typography>
          ))}
        </Alert>
      )}

      <Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={suggestMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
          disabled={!values.name.trim() || suggestMutation.isPending || isLoading}
          onClick={handleSuggest}
        >
          Suggest with AI
        </Button>
      </Box>

      {suggestions && !suggestions.cap_iq_suggestion && !suggestions.zanbato_suggestion && !suggestions.sacra_suggestion && (suggestions.edgar_issuer_names?.length ?? 0) === 0 && !hasConflicts && (
        <Alert severity="info">No suggestions found for this company.</Alert>
      )}

      {suggestions && hasConflicts && (
        <Alert severity="error">
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            The following fields are already in use by another profile:
          </Typography>
          {suggestions.conflicts.map(c => (
            <Typography key={c.field} variant="body2">
              <strong>{c.field}</strong>: "{c.value}" — already used by "{c.in_use_by_profile_name}"
            </Typography>
          ))}
        </Alert>
      )}

      {suggestions?.cap_iq_suggestion && (
        <SuggestionRow
          label="Cap IQ suggestion"
          suggestion={suggestions.cap_iq_suggestion}
          onAccept={handleAccept('cap_iq_id')}
        />
      )}

      {suggestions?.zanbato_suggestion && (
        <SuggestionRow
          label="Zanbato suggestion"
          suggestion={suggestions.zanbato_suggestion}
          onAccept={handleAccept('zanbato_id')}
        />
      )}

      {suggestions?.sacra_suggestion && (
        <SuggestionRow
          label="Sacra domain suggestion"
          suggestion={suggestions.sacra_suggestion}
          onAccept={handleAccept('sacra_domain')}
        />
      )}

      <EdgarIssuerNamesSection
        profileId={profileId}
        suggestedItems={suggestions?.edgar_issuer_names}
        onPendingChange={(toAdd, toRemove) => {
          onPendingEdgarAddsChange?.(toAdd);
          onPendingEdgarRemovesChange?.(toRemove);
        }}
      />
    </Stack>
  );
};


export default CommonCompanyProfileForm;
